<template>
  <div class="settings-account" data-testid="settings-account">
    <!-- Email (read-only) -->
    <div class="sa-row">
      <div class="sa-row-info">
        <span class="sa-row-label">{{ STRINGS.settingsHub.account.email }}</span>
        <span class="sa-row-value" data-testid="settings-account-email">
          <LoadingDots v-if="emailLoading" data-testid="settings-account-email-loading" />
          <span v-else-if="emailError" class="sa-row-value--error">
            {{ STRINGS.settingsHub.account.emailLoadError }}
            <button
              type="button"
              class="sa-email-retry"
              data-testid="settings-account-email-retry"
              @click="loadEmail"
            >
              {{ STRINGS.settingsHub.account.emailRetry }}
            </button>
          </span>
          <template v-else>{{ email }}</template>
        </span>
      </div>
      <span
        v-if="auth.user.value && !auth.isEmailVerified.value"
        class="sa-badge-unverified"
        data-testid="settings-account-email-unverified"
      >
        {{ STRINGS.settingsHub.account.emailUnverified }}
      </span>
    </div>

    <!-- Password -->
    <div class="sa-row">
      <div class="sa-row-info">
        <span class="sa-row-label">{{ STRINGS.settingsHub.account.password }}</span>
        <span class="sa-row-value sa-row-value--masked">
          {{ STRINGS.settingsHub.account.passwordMasked }}
        </span>
      </div>
      <button
        v-if="mode === 'view'"
        class="sa-row-action"
        :aria-label="STRINGS.settingsHub.account.changePasswordAction"
        data-testid="settings-account-change-password-button"
        @click="mode = 'edit-password'"
      >
        {{ STRINGS.settingsHub.account.changeAction }}
      </button>
    </div>

    <!-- Inline password change form -->
    <form
      v-if="mode === 'edit-password'"
      class="sa-form"
      @submit.prevent="handlePasswordSubmit"
      novalidate
    >
      <div class="flex flex-col gap-1.5">
        <label for="settings-current-password" class="text-sm font-semibold text-arena-text-bright">
          {{ STRINGS.settingsHub.account.passwordForm.currentLabel }}
        </label>
        <InputText
          id="settings-current-password"
          v-model="currentPassword"
          type="password"
          class="sa-input min-h-12 touch-manipulation"
          :placeholder="STRINGS.settingsHub.account.passwordForm.currentPlaceholder"
          autocomplete="current-password"
          :aria-invalid="!!passwordError"
          data-testid="settings-account-current-password-input"
          @keyup.enter="handlePasswordSubmit"
        />
      </div>

      <div class="flex flex-col gap-1.5">
        <label for="settings-new-password" class="text-sm font-semibold text-arena-text-bright">
          {{ STRINGS.settingsHub.account.passwordForm.newLabel }}
        </label>
        <InputText
          id="settings-new-password"
          v-model="newPassword"
          type="password"
          class="sa-input min-h-12 touch-manipulation"
          :placeholder="STRINGS.settingsHub.account.passwordForm.newPlaceholder"
          autocomplete="new-password"
          :aria-invalid="!!passwordError"
          data-testid="settings-account-new-password-input"
          @keyup.enter="handlePasswordSubmit"
        />
        <p
          v-if="saveSuccess"
          class="text-xs text-energy-success flex items-center gap-1 m-0"
          role="status"
          aria-live="polite"
          data-testid="settings-account-password-success"
        >
          <AppIcon
            name="checkmark"
            :size="14"
            color="var(--color-energy-success)"
            aria-hidden="true"
          />
          {{ STRINGS.settingsHub.account.passwordForm.success }}
        </p>
        <p v-else class="text-xs text-arena-text-muted m-0">
          {{ STRINGS.settingsHub.account.passwordForm.hint }}
        </p>
      </div>

      <div class="flex flex-col gap-1.5">
        <label for="settings-confirm-password" class="text-sm font-semibold text-arena-text-bright">
          {{ STRINGS.settingsHub.account.passwordForm.confirmLabel }}
        </label>
        <InputText
          id="settings-confirm-password"
          v-model="confirmPassword"
          type="password"
          class="sa-input min-h-12 touch-manipulation"
          :placeholder="STRINGS.settingsHub.account.passwordForm.confirmPlaceholder"
          autocomplete="new-password"
          :aria-invalid="!!passwordError"
          data-testid="settings-account-confirm-password-input"
          @keyup.enter="handlePasswordSubmit"
        />
      </div>

      <!-- Error -->
      <p
        v-if="passwordError"
        class="text-sm text-error-500 text-center m-0"
        role="alert"
        aria-live="polite"
        data-testid="settings-account-password-error"
      >
        {{ passwordError }}
      </p>

      <div class="flex gap-3">
        <PrimeButton
          class="flex-1 min-h-12 touch-manipulation"
          :disabled="!canSavePassword"
          :aria-label="STRINGS.profile.buttons.save"
          data-testid="settings-account-save-password-button"
          @click="handlePasswordSubmit"
        >
          <template v-if="profileData.isLoading.value">
            {{ STRINGS.profile.buttons.save }}<LoadingDots />
          </template>
          <template v-else>
            {{ STRINGS.profile.buttons.save }}
          </template>
        </PrimeButton>
        <PrimeButton
          variant="secondary"
          class="flex-1 min-h-12 touch-manipulation"
          :aria-label="STRINGS.profile.buttons.cancel"
          data-testid="settings-account-cancel-password-button"
          @click="cancelPasswordEdit"
        >
          {{ STRINGS.profile.buttons.cancel }}
        </PrimeButton>
      </div>
    </form>

    <!-- Danger zone -->
    <div class="sa-danger-zone">
      <p class="sa-danger-label">
        {{ STRINGS.profile.labels.dangerZone }}
      </p>

      <PrimeButton
        v-if="mode !== 'delete-account'"
        variant="secondary"
        class="w-full min-h-12 touch-manipulation text-error-500 border-error-500/40 hover:border-error-500"
        :aria-label="STRINGS.aria.labels.deleteAccount"
        data-testid="settings-account-delete-button"
        @click="mode = 'delete-account'"
      >
        {{ STRINGS.profile.buttons.deleteAccount }}
      </PrimeButton>

      <!-- Inline delete confirmation -->
      <form
        v-if="mode === 'delete-account'"
        class="sa-form sa-form--danger"
        @submit.prevent="handleDeleteSubmit"
        novalidate
      >
        <p class="text-sm text-arena-text-muted m-0">
          {{ STRINGS.profile.hints.deleteWarning }}
        </p>

        <div class="flex flex-col gap-1.5">
          <label
            for="settings-delete-password"
            class="text-sm font-semibold text-arena-text-bright"
          >
            {{ STRINGS.profile.labels.password }}
          </label>
          <InputText
            id="settings-delete-password"
            ref="deletePasswordInput"
            v-model="deletePassword"
            type="password"
            class="sa-input min-h-12 touch-manipulation"
            :placeholder="STRINGS.profile.placeholders.password"
            autocomplete="current-password"
            :aria-invalid="!!deleteError"
            data-testid="settings-account-delete-password-input"
            @keyup.enter="handleDeleteSubmit"
          />
        </div>

        <!-- Error -->
        <p
          v-if="deleteError"
          class="text-sm text-error-500 text-center m-0"
          role="alert"
          aria-live="polite"
          data-testid="settings-account-delete-error"
        >
          {{ deleteError }}
        </p>

        <div class="flex gap-3">
          <PrimeButton
            class="flex-1 min-h-12 touch-manipulation text-error-500"
            :disabled="!canDelete"
            :aria-label="STRINGS.aria.labels.confirmDeleteAccount"
            data-testid="settings-account-confirm-delete-button"
            @click="handleDeleteSubmit"
          >
            <template v-if="profileData.isLoading.value && mode === 'delete-account'">
              {{ STRINGS.profile.buttons.deleteAccount }}<LoadingDots />
            </template>
            <template v-else>
              {{ STRINGS.profile.buttons.confirmDelete }}
            </template>
          </PrimeButton>
          <PrimeButton
            variant="secondary"
            class="flex-1 min-h-12 touch-manipulation"
            :aria-label="STRINGS.aria.labels.cancelDeleteAccount"
            data-testid="settings-account-cancel-delete-button"
            @click="cancelDelete"
          >
            {{ STRINGS.profile.buttons.cancel }}
          </PrimeButton>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';
import InputText from 'primevue/inputtext';
import PrimeButton from '../ui/PrimeButton.vue';
import LoadingDots from '../ui/LoadingDots.vue';
import AppIcon from '../ui/icons/AppIcon.vue';
import { useAuth } from '@/composables/useAuth';
import { useProfile } from '@/composables/useProfile';
import { isAuthError } from '@/types/auth';
import { AUTH_CONFIG } from '@/config/constants';
import { reportClientError, ErrorLocation } from '@/telemetry/errors';
import STRINGS from '@/constants/strings';

const auth = useAuth();
const profileData = useProfile();

const mode = ref<'view' | 'edit-password' | 'delete-account'>('view');

// Email is fetched lazily from GET /api/auth/account (it is not part of the
// resident auth identity object) when this tab mounts.
const email = ref('');
const emailLoading = ref(true);
const emailError = ref(false);

// Delay before the one-shot automatic retry of the email fetch (#290).
const EMAIL_RETRY_DELAY_MS = 600;

const currentPassword = ref('');
const newPassword = ref('');
const confirmPassword = ref('');
const passwordError = ref('');
const saveSuccess = ref(false);

const deletePassword = ref('');
const deleteError = ref('');
const deletePasswordInput = ref();

// Handle for the success-message auto-dismiss timer, captured so it can be
// cleared if the form is cancelled/reopened before it fires, and on unmount.
let successTimer: ReturnType<typeof setTimeout> | null = null;

function clearSuccessTimer(): void {
  if (successTimer !== null) {
    clearTimeout(successTimer);
    successTimer = null;
  }
}

// Pending one-shot retry timer for the email fetch, cleared on unmount.
let emailRetryTimer: ReturnType<typeof setTimeout> | null = null;

function clearEmailRetryTimer(): void {
  if (emailRetryTimer !== null) {
    clearTimeout(emailRetryTimer);
    emailRetryTimer = null;
  }
}

// Fetch the account email. A transient failure (network blip / momentary 401)
// is retried once automatically after a short delay before the error and the
// manual Retry button are surfaced. The Retry button calls this again. (#290)
async function loadEmail(): Promise<void> {
  clearEmailRetryTimer();
  emailError.value = false;
  emailLoading.value = true;

  try {
    email.value = await profileData.fetchAccountEmail();
  } catch (firstErr: unknown) {
    reportClientError(firstErr, ErrorLocation.SettingsAccountEmailFetchFailed);
    try {
      await new Promise<void>(resolve => {
        emailRetryTimer = setTimeout(() => {
          emailRetryTimer = null;
          resolve();
        }, EMAIL_RETRY_DELAY_MS);
      });
      email.value = await profileData.fetchAccountEmail();
    } catch (retryErr: unknown) {
      emailError.value = true;
      reportClientError(retryErr, ErrorLocation.SettingsAccountEmailFetchFailed);
    }
  } finally {
    emailLoading.value = false;
  }
}

onMounted(loadEmail);

onUnmounted(() => {
  clearSuccessTimer();
  clearEmailRetryTimer();
});

const canSavePassword = computed(() => {
  return (
    currentPassword.value.length > 0 &&
    newPassword.value.length >= AUTH_CONFIG.PASSWORD_MIN_LENGTH &&
    newPassword.value === confirmPassword.value &&
    !profileData.isLoading.value &&
    !saveSuccess.value
  );
});

const canDelete = computed(() => {
  return deletePassword.value.length > 0 && !profileData.isLoading.value;
});

async function handlePasswordSubmit(): Promise<void> {
  if (!canSavePassword.value) {
    // Surface the most actionable client-side reason before hitting the server.
    if (
      newPassword.value.length > 0 &&
      confirmPassword.value.length > 0 &&
      newPassword.value !== confirmPassword.value
    ) {
      passwordError.value = STRINGS.settingsHub.account.errors.mismatch;
    }
    return;
  }

  passwordError.value = '';
  profileData.clearError();

  try {
    await profileData.changePassword(currentPassword.value, newPassword.value);
    currentPassword.value = '';
    newPassword.value = '';
    confirmPassword.value = '';
    saveSuccess.value = true;
    clearSuccessTimer();
    successTimer = setTimeout(() => {
      saveSuccess.value = false;
      mode.value = 'view';
      successTimer = null;
    }, 2000);
  } catch (err: unknown) {
    if (isAuthError(err)) {
      if (err.type === 'invalid_credentials') {
        passwordError.value = STRINGS.settingsHub.account.errors.wrongPassword;
      } else if (err.type === 'validation') {
        passwordError.value = STRINGS.settingsHub.account.errors.weakPassword;
      } else {
        passwordError.value = err.message;
      }
    } else {
      passwordError.value = STRINGS.auth.errors.network;
    }
  }
}

function cancelPasswordEdit(): void {
  clearSuccessTimer();
  mode.value = 'view';
  currentPassword.value = '';
  newPassword.value = '';
  confirmPassword.value = '';
  passwordError.value = '';
  saveSuccess.value = false;
  profileData.clearError();
}

async function handleDeleteSubmit(): Promise<void> {
  if (!canDelete.value) return;

  deleteError.value = '';
  profileData.clearError();

  try {
    await profileData.deleteAccount(deletePassword.value);
  } catch (err: unknown) {
    if (isAuthError(err)) {
      deleteError.value =
        err.type === 'invalid_credentials' ? STRINGS.profile.errors.wrongPassword : err.message;
    } else {
      deleteError.value = STRINGS.auth.errors.network;
    }
  }
}

function cancelDelete(): void {
  mode.value = 'view';
  deletePassword.value = '';
  deleteError.value = '';
  profileData.clearError();
}
</script>

<style scoped>
.settings-account {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.sa-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.875rem 1rem;
  background: var(--color-arena-void);
  border: 1px solid var(--color-arena-border);
  border-radius: 0.75rem;
}

.sa-row-info {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  min-width: 0;
}

.sa-row-label {
  font-size: 0.6875rem;
  font-weight: 600;
  color: var(--color-arena-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.sa-row-value {
  font-size: 0.875rem;
  color: var(--color-arena-text-bright);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sa-row-value--masked {
  letter-spacing: 0.1em;
  color: var(--color-arena-text-dim);
}

.sa-row-value--error {
  color: var(--color-arena-text-muted);
  font-style: italic;
}

.sa-email-retry {
  margin-left: 0.5rem;
  background: transparent;
  border: none;
  padding: 0;
  font-family: inherit;
  font-size: inherit;
  font-style: normal;
  font-weight: 600;
  color: var(--color-energy-primary);
  cursor: pointer;
  text-decoration: underline;
}

.sa-email-retry:hover {
  color: var(--color-arena-text-bright);
}

.sa-badge-unverified {
  font-size: 0.625rem;
  font-weight: 700;
  color: var(--color-energy-warning, #d9a441);
  background: rgba(217, 164, 65, 0.12);
  border: 1px solid rgba(217, 164, 65, 0.3);
  border-radius: 0.375rem;
  padding: 0.25rem 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  flex-shrink: 0;
}

.sa-row-action {
  font-family: var(--font-sans);
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--color-energy-primary);
  background: transparent;
  border: 1px solid rgba(201, 168, 76, 0.3);
  border-radius: 0.375rem;
  padding: 0.375rem 0.75rem;
  cursor: pointer;
  transition:
    border-color 150ms ease,
    background-color 150ms ease;
  flex-shrink: 0;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.sa-row-action:hover {
  border-color: var(--color-energy-primary);
  background: rgba(201, 168, 76, 0.06);
}

.sa-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem;
  border-radius: 0.75rem;
  border: 1px solid var(--color-arena-border);
  background: rgba(26, 22, 18, 0.8);
}

.sa-form--danger {
  border-color: rgba(239, 68, 68, 0.3);
}

.sa-input {
  padding: 0.75rem 1rem;
  border: 2px solid var(--color-arena-border);
  border-radius: var(--radius-input, 0.5rem);
  font-size: 1rem;
  transition: border-color 150ms ease-out;
  outline: none;
  background: rgba(26, 22, 18, 0.8);
  color: var(--color-arena-text-bright);
  width: 100%;
}

.sa-input:focus {
  border-color: var(--color-primary-500);
  box-shadow: 0 0 0 3px var(--color-primary-500);
}

.sa-input:focus-visible {
  outline: 3px solid var(--color-primary-500);
  outline-offset: 2px;
}

.sa-input::placeholder {
  color: var(--color-arena-text-muted);
}

.sa-danger-zone {
  border-top: 1px solid rgba(239, 68, 68, 0.2);
  padding-top: 1.5rem;
}

.sa-danger-label {
  font-size: 0.6875rem;
  color: var(--color-arena-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.12em;
  margin: 0 0 1rem;
}
</style>
